import React, { useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { observer } from 'mobx-react';
import { Button, ButtonGroup, Flex, Menu, MenuDivider, MenuItem, Popover, Text, Tooltip } from 'core/components';
import { FormState, RemoveMenuItem } from 'core/form';
import { Classes, PopoverPosition } from '@blueprintjs/core';
import styled from 'styled-components';
import { BOARD_LABEL } from '@kentik/ui-shared/boards/boards';
import { FiShare2 } from 'react-icons/fi';
import { showErrorToast } from 'core/components/toast';
import { getHashForObject } from 'app/stores/query/urlHash';
import { withQueryHash } from 'app/util/queryHashParam';
import ShareViewDialog from 'app/views/core/ShareViewDialog';
import { QueryFieldsInput } from '@kentik/ui-shared/boards/panels';
import BoardModel from '../../stores/boards/BoardModel';
import BoardPanelModel from '../../stores/boards/BoardPanelModel';
import { AddPanelButton } from './AddPanelButton';
import AddToBoardDialog from './AddToBoardDialog';
import { toHashPayload } from './udeQueryHash';
import { getQueryFieldsSignature } from './panels/ude/queryFieldsSignature';

const Wrapper = styled.div`
  // force buttons to be shorter
  .${Classes.BUTTON} {
    min-height: 26px;
  }
`;

interface Props {
  board: BoardModel;
  panel?: BoardPanelModel;
  form?: FormState;
  showAddButton?: boolean;
  onSave: () => Promise<void> | void;
  onAddPanel: (type: string) => Promise<void> | void;
  onCancel: () => void;
  onShowSettings: () => void;
}

type SubnavControlsParams = {
  board: BoardModel;
  panel?: BoardPanelModel;
  form?: FormState;
};

type SubnavControls = {
  canEdit: boolean;
  isUdePanel: boolean;
  hasUnsavedChanges: boolean;
  showSaveActions: boolean;
  hasUnappliedQueryChanges: boolean;
  isSaving: boolean;
  isSaveEnabled: boolean;
  isAddEnabled: boolean;
};

function getSubnavControls({ board, panel, form }: SubnavControlsParams): SubnavControls {
  const { canEdit } = board;
  const isFormValid = form ? form.valid : true;
  const hasUnsavedChanges = panel ? panel.isDirty || !!form?.dirty : board.isDirty;
  const isReady = panel ? panel.isReady : true;
  const isUdePanel = panel?.get('type') === 'ude';
  const showSaveActions = !isUdePanel || !!panel?.queryModel?.hasRun;

  const formData = (form?.getValues() as { config?: { data?: QueryFieldsInput } })?.config?.data;
  const appliedSignature = panel?.lastAppliedQuerySignature;
  const hasUnappliedQueryChanges =
    isUdePanel && !!form && appliedSignature !== undefined && getQueryFieldsSignature(formData) !== appliedSignature;

  const isSaving =
    (!board.get('draft') &&
      !board.isSilentSaving &&
      (board.isRequestActive('creating') || board.isRequestActive('updating'))) ||
    (!!panel && !panel.get('draft') && (panel.isRequestActive('creating') || panel.isRequestActive('updating')));

  return {
    canEdit,
    isUdePanel,
    hasUnsavedChanges,
    showSaveActions,
    hasUnappliedQueryChanges,
    isSaving,
    isSaveEnabled: isReady && isFormValid && hasUnsavedChanges && !hasUnappliedQueryChanges,
    isAddEnabled: isReady && isFormValid && !hasUnappliedQueryChanges
  };
}

const BoardSubnavTools = ({
  board,
  panel,
  form,
  showAddButton = true,
  onSave,
  onAddPanel,
  onCancel,
  onShowSettings
}: Props) => {
  const history = useHistory();
  const location = useLocation();
  const [showAddToExistingDialog, setShowAddToExistingDialog] = useState(false);
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [isSharing, setIsSharing] = useState(false);
  const isPanelView = !!panel;

  const {
    canEdit,
    isUdePanel,
    hasUnappliedQueryChanges,
    hasUnsavedChanges,
    isAddEnabled,
    isSaveEnabled,
    isSaving,
    showSaveActions
  } = getSubnavControls({ board, panel, form });

  const basePath = history.location.pathname.startsWith('/v4/ude') ? '/v4/ude' : '/v4/library';

  const handleSave = async () => {
    if (hasUnappliedQueryChanges) {
      return;
    }

    if (panel && form) {
      const values = form.getValues();
      panel.safeSet(values);
    }

    await onSave();
  };

  const handleAddPanel = async (type: string) => {
    if (panel && form) {
      const values = form.getValues();
      panel.safeSet(values);
    }
    await onAddPanel(type);
  };

  const handleAddToBoard = async () => {
    if (panel && form) {
      const values = form.getValues();
      panel.safeSet(values);
    }

    const boardConfig = board.get('config');
    await board.save({ config: { ...boardConfig, isDashboard: true } });
    history.push({ pathname: `${basePath}/boards/${board.id}`, search: location.search });
  };

  const handleBackToBoard = () => {
    history.push({ pathname: `${basePath}/boards/${board.id}`, search: location.search });
  };

  const handleRevertBoard = async () => {
    await board.discardDraftPanels();
  };

  const handleDuplicate = async () => {
    const duplicateBoard = board.duplicate({ save: false });
    await duplicateBoard.save();
    history.push(duplicateBoard.navigatePath, { isEditing: true });
  };

  const handleRemove = async () => {
    await board.destroy();
    history.push('/v4/library');
  };

  const handleShare = async () => {
    const queryModel = panel?.queryModel;
    if (!queryModel) {
      return;
    }
    setIsSharing(true);
    try {
      const payload = toHashPayload({ query: queryModel.query, chart: queryModel.settings.chart });
      const hash = await getHashForObject(payload, undefined, { persist: true });
      setShareUrl(withQueryHash('/v4/ude', hash));
    } catch (error) {
      console.warn('Failed to mint UDE share hash', error);
      showErrorToast('Unable to create a share link. Please try again.');
    } finally {
      setIsSharing(false);
    }
  };

  const showActionsMenu = canEdit && !isPanelView;
  const showAddPanel = !isPanelView && showAddButton && canEdit;
  const showRevert = canEdit && !board.isNew && !isPanelView && board.isDirty;

  const actionsMenu = (
    <Menu>
      <MenuItem icon="cog" text="Settings" onClick={onShowSettings} />
      <MenuItem icon="duplicate" text="Duplicate" onClick={handleDuplicate} />
      {board.canRemove && (
        <>
          <MenuDivider />
          <RemoveMenuItem
            model={board}
            entityName={board.typeLabel}
            text="Delete"
            onRemove={handleRemove}
            confirmButtonText="Delete"
          />
        </>
      )}
    </Menu>
  );

  const saveMenu = (
    <Menu>
      <MenuItem
        icon="undo"
        text="Revert"
        disabled={!board.isDirty || board.isRequestActive('destroying')}
        onClick={handleRevertBoard}
      />
    </Menu>
  );

  return (
    <Wrapper>
      <Flex alignItems="center" gap={1}>
        {showActionsMenu && (
          <Popover
            content={actionsMenu}
            position={PopoverPosition.BOTTOM_RIGHT}
            modifiers={{ offset: { enabled: true, offset: '0,2px' } }}
          >
            <Button rightIcon="caret-down" minimal text="Actions" data-testid="BoardActionsButton" />
          </Popover>
        )}

        {showAddPanel && (
          <AddPanelButton
            board={board}
            icon="plus"
            minimal
            text="Add Panel"
            disabled={!isAddEnabled}
            onAddPanel={handleAddPanel}
          />
        )}
        {isPanelView && board.singlePanelView && canEdit && showSaveActions && (
          <>
            <ButtonGroup>
              <Button text={`Add to ${BOARD_LABEL}`} disabled={!isAddEnabled} onClick={handleAddToBoard} />
              <Popover
                content={
                  <Menu>
                    <MenuItem
                      text={`Existing ${BOARD_LABEL}`}
                      icon="insert"
                      onClick={() => setShowAddToExistingDialog(true)}
                    />
                  </Menu>
                }
                position={PopoverPosition.BOTTOM_RIGHT}
                modifiers={{ offset: { enabled: true, offset: '0,2px' } }}
                disabled={!isAddEnabled}
              >
                <Button icon="caret-down" disabled={!isAddEnabled} aria-label={`Add to ${BOARD_LABEL} options`} />
              </Popover>
            </ButtonGroup>
            {panel && (
              <AddToBoardDialog
                isOpen={showAddToExistingDialog}
                panel={panel}
                onClose={() => setShowAddToExistingDialog(false)}
              />
            )}
          </>
        )}
        {isPanelView && !board.singlePanelView && (
          <Button text={`Back to ${BOARD_LABEL}`} minimal onClick={handleBackToBoard} />
        )}
        {isPanelView && isUdePanel && (
          <>
            <Button
              icon={FiShare2}
              minimal
              text="Share"
              disabled={!panel?.queryModel?.hasRun}
              loading={isSharing}
              onClick={handleShare}
            />
            {shareUrl && (
              <ShareViewDialog
                isOpen
                hideEmail
                hideSubscription
                legacyLinkShare
                privateShareUrl={shareUrl}
                onClose={() => setShareUrl(null)}
              />
            )}
          </>
        )}
        {canEdit &&
          showSaveActions &&
          (isPanelView ? (
            <ButtonGroup>
              <Tooltip
                content={hasUnappliedQueryChanges ? 'Run the query to save your changes' : 'You have unsaved changes'}
                disabled={!hasUnappliedQueryChanges && !hasUnsavedChanges}
                position={PopoverPosition.BOTTOM}
              >
                <Button
                  text="Save"
                  icon={hasUnsavedChanges ? 'dot' : undefined}
                  iconColor="primary"
                  disabled={!isSaveEnabled}
                  loading={isSaving}
                  onClick={handleSave}
                />
              </Tooltip>
              {!panel.isNew && !(board.singlePanelView && panel.hasOnlyDraftVersion) && (
                <Popover
                  content={
                    <Menu>
                      <MenuItem icon="undo" text="Discard Changes" disabled={!panel.isDirty} onClick={onCancel} />
                    </Menu>
                  }
                  position={PopoverPosition.BOTTOM_RIGHT}
                  modifiers={{ offset: { enabled: true, offset: '0,2px' } }}
                  disabled={!panel.isDirty}
                >
                  <Button rightIcon="caret-down" disabled={!panel.isDirty} />
                </Popover>
              )}
            </ButtonGroup>
          ) : (
            <ButtonGroup>
              <Tooltip
                content="You have unsaved changes"
                disabled={!hasUnsavedChanges}
                position={PopoverPosition.BOTTOM}
              >
                <Button
                  text={isSaving ? <Text muted>Saving...</Text> : 'Save'}
                  icon={hasUnsavedChanges && !isSaving ? 'dot' : null}
                  fontWeight={500}
                  iconColor="primary"
                  disabled={!isSaveEnabled}
                  onClick={handleSave}
                  minWidth={80}
                />
              </Tooltip>
              <Popover
                content={saveMenu}
                position={PopoverPosition.BOTTOM_RIGHT}
                modifiers={{ offset: { enabled: true, offset: '0,2px' } }}
                disabled={!showRevert}
              >
                <Button rightIcon="caret-down" disabled={!showRevert} data-testid="BoardSaveMenuButton" />
              </Popover>
            </ButtonGroup>
          ))}
      </Flex>
    </Wrapper>
  );
};

export default observer(BoardSubnavTools);
